/** Responsibility: Validate world simulation integrity (duplicate NPC IDs, orphan locations, titleless quests).
 *  Scope: Supervisor check only; reads state, never mutates. */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

export const worldSimCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  const worldNpcs = state.world?.npcs
  if (Array.isArray(worldNpcs)) {
    const duplicateIds = new Set<string>()
    const seenIds = new Set<string>()
    for (const npc of worldNpcs) {
      const id = String(npc?.id ?? '').trim()
      if (!id) continue
      if (seenIds.has(id)) duplicateIds.add(id)
      else seenIds.add(id)
    }
    if (duplicateIds.size > 0) {
      incidents.push({
        code: 'worldsim_duplicate_npc_ids',
        domain: 'object',
        severity: 'medium',
        confidence: 0.95,
        message: `${duplicateIds.size} duplicate world NPC IDs found.`,
        remediationPlan: 'Deduplicate world NPCs by ID, keeping the latest entry.',
        context: { duplicateIds: [...duplicateIds] },
      })
    }

    const orphanNpcs = worldNpcs.filter(npc => {
      const loc = String(npc?.location ?? '').trim()
      if (!loc) return false
      return !state.atlas?.nodes?.[loc]
    })
    if (orphanNpcs.length > 0) {
      incidents.push({
        code: 'worldsim_npc_orphan_location',
        domain: 'object',
        severity: 'low',
        confidence: 0.8,
        message: `${orphanNpcs.length} world NPCs reference atlas locations that don't exist.`,
        remediationPlan: 'Remove location from orphaned NPCs or add missing atlas nodes.',
        context: { orphanCount: orphanNpcs.length },
      })
    }
  }

  const journal = state.journal
  if (Array.isArray(journal)) {
    const invalidQuests = journal.filter(q => !q?.title?.trim())
    if (invalidQuests.length > 0) {
      incidents.push({
        code: 'worldsim_quest_no_title',
        domain: 'quest',
        severity: 'low',
        confidence: 0.9,
        message: `${invalidQuests.length} journal quests have no title.`,
        remediationPlan: 'Prune quests with empty titles.',
      })
    }
  }

  return { passed: incidents.length === 0, incidents }
}
